import { useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router";
import { useAuth } from "../../context/AuthContext";

/**
 * Opaque full-viewport layer while the protected shell is not ready.
 * Avoids a flash of underlying UI (e.g. OS theme gradients) before layout loading states mount.
 *
 * @param testId - `data-testid` for tests
 * @param ariaLabel - Accessible status label for screen readers
 */
export function ProtectedShellBackdrop({
  testId,
  ariaLabel,
}: {
  testId: string;
  ariaLabel: string;
}) {
  return (
    <div
      data-testid={testId}
      className="fixed inset-0 z-[100] bg-slate-900"
      role="status"
      aria-busy="true"
      aria-label={ariaLabel}
    />
  );
}

/**
 * Renders `children` only when the user is authenticated; shows a full-viewport backdrop while auth is loading.
 */
export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuthAndToken = async () => {
      if (!isLoading && !isAuthenticated) {
        // Try multiple times to find the token
        let hasToken = false;
        let attempts = 0;
        const maxAttempts = 5;

        while (!hasToken && attempts < maxAttempts) {
          hasToken =
            !!localStorage.getItem("auth_data") ||      // Bearer token (non-SSO users)
            !!localStorage.getItem("workos_user");      // Session cookie (SSO users)
          if (!hasToken) {
            await new Promise((resolve) => setTimeout(resolve, 200));
            attempts++;
          }
        }

        if (!hasToken) {
          navigate("/login", {
            replace: true,
            state: { from: location },
          });
        } else {
          // Wait a bit longer for auth state to update
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }
    };

    checkAuthAndToken();
  }, [isAuthenticated, isLoading, navigate, location]);

  // Memoize the rendered content to prevent unnecessary re-renders
  const renderedContent = useMemo(() => {
    if (isLoading) {
      return (
        <ProtectedShellBackdrop
          testId="protected-route-loading"
          ariaLabel="Checking authentication"
        />
      );
    }

    return isAuthenticated ? children : null;
  }, [isLoading, isAuthenticated, children]);

  return renderedContent;
}
